import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';
import {
  Interpretation,
  DraftSaveResponse,
  PendingTestDataResponse,
  SLAConfig,
  CompanySLA,
  PaginatedResponse,
  PaginationParams,
  NotificationsResponse,
  Notification,
  AuditAction,
  AuditMetadata,
  AuditTimeline,
  AuditMetrics,
  RecordAuditEventResponse,
  RejectionReason,
  CostsSummary,
  CostsBreakdownResponse,
  CurrentPricing,
  CostPeriod,
  DeliveryPolicy,
  SearchResponse,
  CompaniesResponse,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// MSAL instance for token acquisition
let msalInstance: PublicClientApplication | null = null;

// Token cache to prevent multiple concurrent acquireTokenSilent calls
let tokenCache: { idToken: string; accessToken: string; expiresAt: number } | null = null;
let tokenPromise: Promise<{ idToken: string | null; accessToken: string | null }> | null = null;

// Refresh token function injected from AuthContext
let refreshTokenFn: (() => Promise<boolean>) | null = null;

/**
 * Initialize MSAL instance for API authentication
 */
export const initializeApiAuth = (instance: PublicClientApplication): void => {
  msalInstance = instance;
  // Clear cache when instance changes
  tokenCache = null;
  tokenPromise = null;
};

/**
 * Set the refresh token function from AuthContext
 */
export const setRefreshTokenFunction = (fn: () => Promise<boolean>): void => {
  refreshTokenFn = fn;
};

/**
 * Clear token cache (used when refresh is needed)
 */
export const clearTokenCache = (): void => {
  tokenCache = null;
  tokenPromise = null;
};

/**
 * Get tokens from MSAL for API authentication
 * - ID token: Used for API Gateway authorizer (audience = our client ID)
 * - Access token: Used for Microsoft Graph API calls (fetching user photo)
 *
 * Uses caching and deduplication to prevent multiple concurrent token requests
 */
const getTokens = async (): Promise<{ idToken: string | null; accessToken: string | null }> => {
  if (!msalInstance) {
    console.warn('MSAL instance not initialized');
    return { idToken: null, accessToken: null };
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return { idToken: null, accessToken: null };
  }

  // Check if cached token is still valid (with 5 minute buffer)
  const now = Date.now();
  if (tokenCache && tokenCache.expiresAt > now + 5 * 60 * 1000) {
    return { idToken: tokenCache.idToken, accessToken: tokenCache.accessToken };
  }

  // If there's already a token request in progress, wait for it
  if (tokenPromise) {
    return tokenPromise;
  }

  // Create a new token request
  tokenPromise = (async () => {
    try {
      const response = await msalInstance!.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        forceRefresh: false,
      });

      // Cache the token with expiration
      tokenCache = {
        idToken: response.idToken,
        accessToken: response.accessToken,
        expiresAt: response.expiresOn ? response.expiresOn.getTime() : now + 60 * 60 * 1000,
      };

      return {
        idToken: response.idToken,
        accessToken: response.accessToken,
      };
    } catch (error: any) {
      // Handle specific MSAL errors gracefully
      if (error.errorCode === 'block_iframe_reload' ||
          error.errorCode === 'timed_out' ||
          error.errorCode === 'interaction_required' ||
          error.name === 'InteractionRequiredAuthError') {
        console.warn('Silent token acquisition failed:', error.errorCode || error.name);
      } else {
        console.error('Failed to acquire token silently:', error);
      }
      return { idToken: null, accessToken: null };
    } finally {
      // Clear the promise so next request can try again
      tokenPromise = null;
    }
  })();

  return tokenPromise;
};

/**
 * Get current user's auditor ID (Azure AD Object ID)
 */
const getAuditorId = (): string | null => {
  if (!msalInstance) {
    return process.env.REACT_APP_AUDITOR_ID || 'auditor-dev';
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  return accounts[0].localAccountId;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
});

// Request interceptor to add Bearer token and Auditor ID
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Get both tokens
    const { idToken, accessToken } = await getTokens();

    // Add Bearer token (ID token) for API Gateway authorizer
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }

    // Add Graph Access Token for fetching user photos
    if (accessToken) {
      config.headers['X-Graph-Token'] = accessToken;
    }

    // Add Auditor ID header
    const auditorId = getAuditorId();
    if (auditorId) {
      config.headers['X-Auditor-ID'] = auditorId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || '';

    // Check if this is a token expiration error (401 or 403 with explicit deny)
    const isTokenExpired =
      status === 401 ||
      (status === 403 && errorMessage.includes('explicit deny'));

    // Handle token expiration - attempt token refresh and retry
    if (isTokenExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      if (refreshTokenFn) {
        // Clear cached tokens
        clearTokenCache();

        // Attempt refresh
        const refreshSuccess = await refreshTokenFn();

        if (refreshSuccess) {
          // Retry original request with new token
          const { idToken, accessToken } = await getTokens();
          if (idToken) {
            originalRequest.headers.Authorization = `Bearer ${idToken}`;
          }
          if (accessToken) {
            originalRequest.headers['X-Graph-Token'] = accessToken;
          }
          return apiClient(originalRequest);
        }
        // If refresh failed, refreshTokenFn already redirected to /login
      } else {
        // No refresh function available, redirect to login
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Interpretations API
export const interpretationsApi = {
  getPending: async (
    params?: PaginationParams
  ): Promise<PaginatedResponse<Interpretation>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.next_token) queryParams.append('next_token', params.next_token);

    const url = `/interpretations/pending${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return {
      items: response.data.items || [],
      count: response.data.count || 0,
      total: response.data.total || response.data.count || 0,
      next_token: response.data.next_token,
    };
  },

  getAll: async (
    params?: PaginationParams & { status?: string }
  ): Promise<PaginatedResponse<Interpretation>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.next_token) queryParams.append('next_token', params.next_token);
    if (params?.status) queryParams.append('status', params.status);

    const url = `/interpretations/all${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return {
      items: response.data.items || [],
      count: response.data.count || 0,
      total: response.data.total || response.data.count || 0,
      next_token: response.data.next_token,
    };
  },

  /**
   * Server-side search over the light index (E06-F01, E06-F02). ONE request per
   * `(q, status, company)`: the server normalises `q` (accents, case, whitespace) and
   * walks its own index, so the client neither lowercases nor sweeps pages. At least
   * one of `q` / `company` must be present -- the caller decides, this only moves what
   * it is given. `status` is the list's filter; `'all'` is the ABSENCE of the
   * parameter, never the literal string. `company` is the option's value, sent as-is:
   * the server compares it by equality against the stored name.
   */
  search: async (params: {
    q?: string;
    status?: string;
    company?: string;
  }): Promise<SearchResponse> => {
    // URLSearchParams percent-encodes every value, so an eñe, an accent or the space
    // inside a company name reaches the server intact instead of as mangled bytes.
    // Each key is appended ONLY when present and non-empty: an empty `q` would be a
    // client-made 400.
    const queryParams = new URLSearchParams();
    if (params.q) queryParams.append('q', params.q);
    if (params.status) queryParams.append('status', params.status);
    if (params.company) queryParams.append('company', params.company);

    const response = await apiClient.get(`/interpretations/search?${queryParams}`);
    return {
      items: response.data.items ?? [],
      count: response.data.count ?? 0,
      truncated: Boolean(response.data.truncated),
      max_results: response.data.max_results,
    };
  },

  /**
   * The companies of the light index, each with how many entries name it (E06-F02).
   * The dropdown's options come from here, never from a page sweep. `companies` is
   * handed back WITHOUT a `?? []` on purpose: a body with no list is a failure the
   * hook must see, not an empty dropdown. A backend without this route answers 404,
   * which the hook reports as a failure too.
   */
  companies: async (params?: { status?: string }): Promise<CompaniesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);

    const url = `/interpretations/companies${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return {
      companies: response.data.companies,
      count: response.data.count ?? 0,
    };
  },

  getById: async (id: string): Promise<Interpretation> => {
    const response = await apiClient.get(`/interpretations/${id}`);
    return response.data;
  },

  approve: async (
    id: string,
    data: {
      final_interpretation?: string;
      action_plan?: any;
      auditor_notes?: string;
      modification_reason?: string;
    }
  ): Promise<Interpretation> => {
    // auditor_id is now extracted from JWT in backend
    const response = await apiClient.post(`/interpretations/${id}/approve`, data);
    return response.data;
  },

  /**
   * Saves the auditor's work in progress.
   *
   * It does NOT approve and it sends NOTHING to the patient: the interpretation stays
   * in `pending_review`, the text lands in `draft_interpretation` (never
   * `final_interpretation`, which is what the PDF is built from), and no report is
   * generated. This is the action the edit modal was missing on 2026-08-11, when the
   * only button labelled "Guardar" mailed a report to a real patient.
   */
  saveDraft: async (
    id: string,
    draft_interpretation: string
  ): Promise<DraftSaveResponse> => {
    const response = await apiClient.post(`/interpretations/${id}/draft`, {
      draft_interpretation,
    });
    return response.data;
  },

  regenerate: async (
    id: string,
    additional_context?: string
  ): Promise<{ status: string }> => {
    const response = await apiClient.post(`/interpretations/${id}/regenerate`, {
      additional_context,
    });
    return response.data;
  },

  /**
   * Mails the report PDF to the patient.
   *
   * Returns `status: 'skipped'` outside production, where delivery is disabled --
   * a declined send, not a failure. Callers must not report it as sent.
   */
  sendReport: async (id: string): Promise<{ status: string; message?: string }> => {
    const response = await apiClient.post(`/interpretations/${id}/send-report`);
    return response.data;
  },

  /**
   * Whether this interpretation's company receives reports by email.
   *
   * Served by the same lambda -- and computed by the same function -- that decides
   * the send itself, so the warning the auditor reads and the behaviour they get
   * cannot drift apart.
   *
   * A FAILURE HERE MEANS *UNKNOWN*, NEVER *DOES NOT DELIVER*: callers must treat a
   * rejected call (or an absent answer) as "keep today's text". Announcing "no mail
   * will go out" because a lookup broke is the exact lie this feature exists to end.
   */
  getDeliveryPolicy: async (id: string): Promise<DeliveryPolicy> => {
    const response = await apiClient.get(`/interpretations/${id}/delivery-policy`);
    return response.data;
  },

  /**
   * Signed URL for the report PDF.
   *
   * `preview` lifts the approved-only gate so an auditor can see the deliverable
   * before approving -- approving is what mails it to the patient. It cannot
   * deliver anything: the PDF is rendered on demand under an `ondemand/` prefix
   * that the (blob-triggered) mailer explicitly skips.
   */
  getReportUrl: async (id: string, preview = false, force = false): Promise<{
    url?: string;
    expiresAt?: string;
    period?: { year: string; month: string };
    /** 'generating' comes back with HTTP 202: the render outlasted the gateway
     *  and Azure is still working. Retry shortly to land on a warm cache. */
    status?: string;
    message?: string;
  }> => {
    const params: Record<string, string> = {};
    if (preview) params.preview = 'true';
    if (force) params.force = 'true';

    const response = await apiClient.get(`/interpretations/${id}/report-url`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  generate: async (data: {
    patient_key: string;
    test_data_ids?: string[];
  }): Promise<{ status: string; execution_arn: string }> => {
    const response = await apiClient.post('/interpretations/generate', data);
    return response.data;
  },

  reject: async (
    id: string,
    reason: RejectionReason,
    comment?: string
  ): Promise<{
    interpretation_id: string;
    status: string;
    rejection_reason: RejectionReason;
    rejection_comment?: string;
    rejected_at: string;
    rejected_by: string;
  }> => {
    const response = await apiClient.post(`/interpretations/${id}/reject`, {
      reason,
      comment,
    });
    return response.data;
  },

  reopen: async (
    id: string,
    body: { reason: string; description?: string; totp_code: string }
  ): Promise<{ interpretation_id: string; status: string }> => {
    const response = await apiClient.post(`/interpretations/${id}/reopen`, body);
    return response.data;
  },

  getGabPdfUrl: async (
    interpretationId: string,
    studyCode: string
  ): Promise<{
    presigned_url: string;
    study_code: string;
    expires_in: number;
  }> => {
    const response = await apiClient.get(
      `/interpretations/${interpretationId}/gab-pdf/${encodeURIComponent(studyCode)}`
    );
    return response.data;
  },
};

// TestData API
export const testDataApi = {
  getPending: async (): Promise<PendingTestDataResponse> => {
    const response = await apiClient.get('/test-data/pending');
    return response.data;
  },
};

// Config API
export const configApi = {
  getGlobalSLA: async (): Promise<SLAConfig> => {
    const response = await apiClient.get('/config/sla');
    return response.data;
  },

  updateGlobalSLA: async (
    sla_hours: number,
    updated_by: string
  ): Promise<SLAConfig> => {
    const response = await apiClient.put('/config/sla', {
      sla_hours,
      updated_by,
    });
    return response.data;
  },

  getCompanySLAs: async (): Promise<{ companies: CompanySLA[]; total: number }> => {
    const response = await apiClient.get('/config/sla/companies');
    return response.data;
  },

  getCompanySLA: async (companyId: string): Promise<CompanySLA> => {
    const response = await apiClient.get(`/config/sla/companies/${companyId}`);
    return response.data;
  },

  updateCompanySLA: async (
    companyId: string,
    sla_hours: number,
    updated_by: string
  ): Promise<CompanySLA> => {
    const response = await apiClient.put(`/config/sla/companies/${companyId}`, {
      sla_hours,
      updated_by,
    });
    return response.data;
  },

  deleteCompanySLA: async (companyId: string): Promise<void> => {
    await apiClient.delete(`/config/sla/companies/${companyId}`);
  },
};

// TTS API
export const ttsApi = {
  getConfig: async (): Promise<{ tts_provider: 'browser' | 'polly' }> => {
    try {
      const response = await apiClient.get('/tts/config');
      return response.data;
    } catch {
      // Default to browser if endpoint not available
      return { tts_provider: 'browser' };
    }
  },

  synthesize: async (
    text: string,
    interpretationId?: string,
    patientId?: string
  ): Promise<{
    audio: string;
    content_type: string;
    voice: string;
    characters?: number;
    polly_cost?: number;
  }> => {
    const response = await apiClient.post('/tts/synthesize', {
      text,
      interpretation_id: interpretationId,
      patient_id: patientId,
    });
    return response.data;
  },
};

// Notifications API
export const notificationsApi = {
  getAll: async (): Promise<NotificationsResponse> => {
    const response = await apiClient.get('/notifications');
    return response.data;
  },

  markAsRead: async (notificationId: string): Promise<Notification> => {
    const response = await apiClient.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async (): Promise<{ updated_count: number }> => {
    const response = await apiClient.put('/notifications/read-all');
    return response.data;
  },

  delete: async (notificationId: string): Promise<void> => {
    await apiClient.delete(`/notifications/${notificationId}`);
  },

  refresh: async (): Promise<NotificationsResponse> => {
    const response = await apiClient.post('/notifications/refresh');
    return response.data;
  },
};

// System Config API (admin only)
export interface SystemConfig {
  tts: {
    provider: 'polly' | 'browser';
    voice: string;
  };
  ai_model: {
    model: string;
  };
}

export interface TTSConfigUpdate {
  provider: 'polly' | 'browser';
  voice: string;
  updated_at?: string;
  updated_by?: string;
}

export interface AIModelConfigUpdate {
  model: string;
  updated_at?: string;
  updated_by?: string;
}

export const systemConfigApi = {
  getSystem: async (): Promise<SystemConfig> => {
    const response = await apiClient.get('/config/system');
    return response.data;
  },

  updateTTS: async (provider: 'polly' | 'browser', voice: string): Promise<TTSConfigUpdate> => {
    const response = await apiClient.put('/config/system/tts', { provider, voice });
    return response.data;
  },

  updateAIModel: async (model: string): Promise<AIModelConfigUpdate> => {
    const response = await apiClient.put('/config/system/ai-model', { model });
    return response.data;
  },

  getAIModels: async (): Promise<{ models: string[] }> => {
    const response = await apiClient.get('/config/ai-models');
    return response.data;
  },
};

// Audit Logs API
export const auditLogsApi = {
  /**
   * Record an audit event (fire-and-forget pattern)
   * Errors are logged but not thrown to avoid blocking UI
   */
  recordEvent: async (
    interpretationId: string,
    action: AuditAction,
    metadata?: AuditMetadata,
    patientId?: string
  ): Promise<RecordAuditEventResponse | null> => {
    try {
      const response = await apiClient.post('/audit-logs', {
        interpretation_id: interpretationId,
        action,
        metadata: metadata || {},
        patient_id: patientId,
      });
      return response.data;
    } catch (error) {
      // Fire-and-forget: log error but don't throw
      console.warn('Failed to record audit event:', error);
      return null;
    }
  },

  /**
   * Get timeline of events for an interpretation
   */
  getTimeline: async (interpretationId: string): Promise<AuditTimeline> => {
    const response = await apiClient.get(`/interpretations/${interpretationId}/audit-logs`);
    return response.data;
  },

  /**
   * Get aggregated metrics
   */
  getMetrics: async (
    startDate: string,
    endDate: string,
    auditorId?: string
  ): Promise<AuditMetrics> => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (auditorId) {
      params.append('auditor_id', auditorId);
    }
    const response = await apiClient.get(`/audit-logs/metrics?${params}`);
    return response.data;
  },
};

// Costs API
export const costsApi = {
  /**
   * Get cost summary for a period
   */
  getSummary: async (params?: {
    period?: CostPeriod;
    year?: number;
    month?: number;
  }): Promise<CostsSummary> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());

    const url = `/costs/summary${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get detailed cost breakdown per interpretation
   */
  getBreakdown: async (params?: {
    period?: CostPeriod;
    year?: number;
    month?: number;
    limit?: number;
    next_token?: string;
  }): Promise<CostsBreakdownResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.next_token) queryParams.append('next_token', params.next_token);

    const url = `/costs/breakdown${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get current pricing configuration
   */
  getPricing: async (): Promise<CurrentPricing> => {
    const response = await apiClient.get('/costs/pricing');
    return response.data;
  },
};

export const adminTotpApi = {
  list: async (): Promise<{ enrolled: { oid: string; name: string; email: string }[] }> => {
    const response = await apiClient.get('/admin/totp');
    return response.data;
  },
  enroll: async (oid: string, name: string, email: string): Promise<{ otpauth_uri: string }> => {
    const response = await apiClient.post('/admin/totp/enroll', { oid, name, email });
    return response.data;
  },
  revoke: async (oid: string): Promise<{ oid: string }> => {
    const response = await apiClient.delete(`/admin/totp/${oid}`);
    return response.data;
  },
};

export default apiClient;
